'use client'

import { useEffect } from 'react'
import { useSearchParams } from 'next/navigation'

async function acceptGuestTokenWithRetry(guestToken: string, maxAttempts = 5) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const res = await fetch('/api/tenants/guest-invites/accept', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: guestToken }),
    })
    const data = await res.json()

    if (res.ok && data.slug) {
      return data.slug as string
    }

    if (res.status !== 401 || attempt === maxAttempts - 1) {
      return null
    }

    await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)))
  }

  return null
}

/**
 * After OAuth sign-in, accept a guest invite token from the URL and land on the club preview.
 */
export function GuestInviteCallbackHandler() {
  const searchParams = useSearchParams()
  const guestToken = searchParams.get('guestToken')

  useEffect(() => {
    if (!guestToken) return

    const accept = async () => {
      const slug = await acceptGuestTokenWithRetry(guestToken)
      if (slug) {
        window.location.href = `/${slug}`
      }
    }

    accept()
  }, [guestToken])

  return null
}

export function buildAuthCallbackUrl(guestToken?: string | null, email?: string | null) {
  const params = new URLSearchParams()
  if (email) params.set('email', email)
  if (guestToken) params.set('guestToken', guestToken)
  const qs = params.toString()
  return qs ? `/dashboard?${qs}` : '/dashboard'
}

export async function acceptGuestTokenAfterSignIn(guestToken: string): Promise<string | null> {
  return acceptGuestTokenWithRetry(guestToken)
}

export function redirectAfterSignupOrInvite(data: {
  redirectSlug?: string | null
  guestPreviewClub?: { slug: string } | null
  invitedClubs?: Array<{ slug: string }>
  guestToken?: string | null
}) {
  const slug =
    data.redirectSlug ??
    data.guestPreviewClub?.slug ??
    data.invitedClubs?.[0]?.slug

  if (slug) {
    window.location.href = `/${slug}`
    return Promise.resolve(slug)
  }

  if (data.guestToken) {
    return acceptGuestTokenAfterSignIn(data.guestToken).then((tokenSlug) => {
      if (tokenSlug) {
        window.location.href = `/${tokenSlug}`
        return tokenSlug
      }
      window.location.href = '/dashboard'
      return null
    })
  }

  window.location.href = '/dashboard'
  return Promise.resolve(null)
}
